import compose from 'lodash/fp/compose';
import { getIn } from 'seamless-immutable';

const fmtUnit = (str = '', unit) => `${str.toString().replace('.', ',')} ${unit}`;

const formatDimensions = dimensions => ({
  weight: fmtUnit(dimensions.weight, 'kg'),
  width: fmtUnit(dimensions.width, 'cm'),
  depth: fmtUnit(dimensions.length, 'cm'),
  height: fmtUnit(dimensions.height, 'cm'),
});

const fmtAttributes = ({ product }) => schema => {
  const attributes = getIn(product, ['attributes'], []);
  const model = attributes && attributes.find(({ name }) => name.toLowerCase() === 'modelo');
  const color = attributes && attributes.find(({ name }) => name.toLowerCase() === 'cor');

  if (model) {
    schema.model = model.value;
  }

  if (color) {
    schema.color = color.value;
  }

  return schema;
};

const fmtCategory = ({ page }) => schema => {
  const breadcrumb = getIn(page, ['breadcrumb']) || [];
  const last = breadcrumb[breadcrumb.length - 1];

  if (last) {
    schema.category = last.name;
  }

  return schema;
};

const fmtAggregateRating = ({ product }) => schema => {
  const average = getIn(product, ['rating', 'average']) || '';
  const reviews = getIn(product, ['rating', 'reviews'], 0);

  if (reviews > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      bestRating: '5',
      ratingCount: reviews.toString(),
      ratingValue: average.toString(),
      worstRating: '1',
    };
  }

  return schema;
};

const getPrice = offer => {
  const bestPayment = getIn(offer, ['bestPaymentOption']);

  if (bestPayment && bestPayment.type === 'BOLETO') {
    return bestPayment.price;
  }

  if (bestPayment && bestPayment.type === 'PIX') {
    return bestPayment.price;
  }

  return getIn(bestPayment, ['minQuantity', '0', 'value']);
};

const fmtEans = ({ currentSku }) => schema => {
  const ean = getIn(currentSku, ['eans', '0']);

  if (ean) {
    schema.gtin13 = ean;
  }

  return schema;
};

const fmtOffer = ({ currentSku, product }) => schema => {
  const totalOffers = getIn(currentSku, ['offers', 'meta', 'total']);
  const offer = getIn(currentSku, ['offers', 'result', '0']);
  const lastOffer = getIn(product, ['offers', 'result', totalOffers - 1]);
  const price = getPrice(offer);

  if (offer) {
    schema.offers = {
      '@type': 'Offer',
      priceCurrency: 'BRL',
      availability: 'http://schema.org/InStock',
      itemCondition: 'http://schema.org/NewCondition',
    };

    schema.offers.price = price && price.toString();

    if (totalOffers > 1) {
      schema.offers['@type'] = 'AggregateOffer';
      schema.offers.lowPrice = price && price.toString();
      schema.offers.highPrice = getIn(lastOffer, ['salesPrice'], '').toString();
      schema.offers.offerCount = totalOffers.toString();
    }
  } else {
    schema.offers = {
      availability: 'http://schema.org/OutOfStock',
    };
  }

  return schema;
};

const fmtReview = ({ badges, createdAt, feedback, owner, id, rating, text }) => ({
  '@type': 'Review',
  badges,
  createdAt: new Date(createdAt).toLocaleDateString('pt-BR'),
  feedback: {
    downVotes: feedback?.downVotes,
    upVotes: feedback?.upVotes,
    '@type': 'Feedback',
  },
  owner: {
    id: owner?.id,
    name: owner?.name,
    '@type': 'Owner',
  },
  id,
  rating,
  text: {
    description: text?.description,
    title: text?.title,
    '@type': 'Text',
  },
});

const getReviews = ({ reviewsQueries }) => schema => {
  const queryReviews = getIn(reviewsQueries, ['data']);

  if (queryReviews?.reviewsStats) {
    const { rating } = queryReviews.reviewsStats;
    const { calculated, count, distribution } = rating;

    schema.review = {
      ReviewStats: {
        '@type': 'Rating',
        rating: {
          '@type': 'ReviewStats',
          calculated,
          count,
          distribution: {
            ...distribution,
            '@type': 'Distribution',
          },
        },
      },
    };
  }

  if (queryReviews?.reviews) {
    const { meta, result } = queryReviews.reviews;

    schema.review = {
      ...schema.review,
      '@type': 'Reviews',
      reviews: {
        '@type': 'Result',
        result: result?.map(fmtReview),
        meta: {
          '@type': 'Total',
          total: meta?.total,
        },
      },
    };
  }

  return schema;
};

export const getProductDefault = ({ config, currentSku, product, page }) => {
  const { domainUrl } = config;
  const { id, name, supplier } = product || {};
  const { description, url } = page ? page : {};
  const skuId = getIn(currentSku, ['id']);
  const dimensions = getIn(currentSku, ['dimensions']);
  const image = getIn(currentSku, ['images', '0', 'large']);

  let schema = {
    '@type': 'Product',
    '@id': `/produto/${id}/#produto`,
    mainEntityOfPage: {
      '@id': `/produto/${id}/#webpage`,
    },
    name,
    image: {
      '@type': 'ImageObject',
      '@id': `/produto/${id}/#primaryimage`,
      url: image,
      caption: name,
    },
    description,
    url: `${domainUrl}${url}`,
  };

  if (skuId) {
    schema.sku = skuId;
  }

  if (dimensions) {
    schema = Object.assign({}, schema, formatDimensions(dimensions));
  }

  if (supplier) {
    schema.brand = supplier;
  }

  return schema;
};

export const getProductSchema = props => {
  const initialSchema = getProductDefault(props);

  return compose(
    fmtAttributes(props),
    fmtCategory(props),
    fmtAggregateRating(props),
    fmtOffer(props),
    fmtEans(props),
    getReviews(props)
  )(initialSchema);
};
